import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Panel } from '../ui/Panel';
import { useAdmin } from '../AdminContext';
import { fetchAgenda, fetchKassaDay } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { lastColleagueDay, staffDaySummary } from '../../core/rules/staffDaySummary';
import { staffList, staffName } from '../../core/rules/salonDays';
import { money } from '../../core/format/currency';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { dayLong, shiftDate, today } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { ADMIN_BASE } from '../../utils/hosts';
import type { RequestOptions } from '../../core/types/backend';

/** Zo ver vooruit tellen open aanvragen voor haar dagen mee. */
const REQUESTS_AHEAD = 28;

/**
 * Op Sarahs overzicht: hoe de laatste dag van de collega ging. Klanten, niet gekomen, verkoop op
 * haar bonnen, en wat er nog openstaat (niet afgerekend, aanvragen). Geen collegadag in de
 * afgelopen week: niets.
 */
export function StaffDaySummaryPanel() {
  const {
    site
  } = useAdmin();
  const now = today();
  const last = lastColleagueDay(site.openingHours, site.salonExceptions, now);
  const load = useCallback(async (opts: RequestOptions) => {
    if (!last) return null;
    const [agenda, kassa] = await Promise.all([fetchAgenda(last.date, shiftDate(now, REQUESTS_AHEAD), opts), fetchKassaDay(last.date, opts)]);
    return staffDaySummary(agenda.appointments, kassa.sales, last.staffId, last.date, now);
  },
  // `last` volgt uit datum en instellingen: op de waarden, niet op het object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [last?.date, last?.staffId, now]);
  const {
    data: s,
    status,
    reload
  } = useLatestLoad(load);
  if (!last) return null;
  const name = staffName(staffList(site.staff), last.staffId);
  const running = last.date === now;
  return <Panel title={`Dag van ${name}`} description={`${running ? 'Vandaag (nog bezig)' : dayLong(last.date)}`} data-id="element-1614" data-mp="29q5wpkgzkdr0">
      {status === 'error' && !s ? <LoadFailed variant="inline" title="De dag laden lukte niet" onRetry={reload} data-id="element-1615" data-mp="23ansdsejkssg" /> : !s ? <LoadingLine data-id="element-1616" data-mp="1yat31taf0b9i" /> : <>
          <dl className="grid grid-cols-3 gap-3" data-id="element-1617" data-mp="1ybl078jnuh1w">
            <div data-id="element-1618" data-mp="1tcrkd9gwidyk">
              <dt className="text-[12px] text-muted" data-id="element-1619" data-mp="1td9r5016wakq">Klanten</dt>
              <dd className="font-display text-[1.6rem] font-light leading-none tabular-nums text-ink" data-id="element-1620" data-mp="2r00a5gnxq96d">{s.clients}</dd>
            </div>
            <div data-id="element-1621" data-mp="2m1ykgcw07blx">
              <dt className="text-[12px] text-muted" data-id="element-1622" data-mp="2m1fapjgibtiz">Niet gekomen</dt>
              <dd className="font-display text-[1.6rem] font-light leading-none tabular-nums text-ink" data-id="element-1623" data-mp="2h9f4j99ug4dy">{s.noShows}</dd>
            </div>
            <div data-id="element-1624" data-mp="2cau4b9uldkpi">
              <dt className="text-[12px] text-muted" data-id="element-1625" data-mp="2c99kae5wlk5w">Verkoop</dt>
              <dd className="font-display text-[1.6rem] font-light leading-none tabular-nums text-ink" data-id="element-1626" data-mp="39r9ktt9fz3zj">{money(s.salesCents)}</dd>
            </div>
          </dl>
          {s.unpaid.length === 0 && s.openRequests.length === 0 ? <p className="mt-4 text-[13px] text-muted" data-id="element-1627" data-mp="3cdad7p8jd4ox">Niets meer open.</p> : <ul className="mt-4 space-y-1.5 text-[13px]" data-id="element-1628" data-mp="3hc3gkou3zfbd">
              {s.unpaid.length > 0 && <li data-id="element-1629" data-mp="3mb6nyfxk9xmi">
                  <Link to={running ? `${ADMIN_BASE}/kassa` : `${ADMIN_BASE}/agenda?datum=${last.date}`} className="text-clay underline-offset-4 hover:underline" data-id="element-1630" data-mp="3r86t8oml6wck">
                    {plural(s.unpaid.length, 'afspraak', 'afspraken')} nog niet afgerekend
                  </Link>
                  <span className="text-muted" data-id="element-1631" data-mp="41e5b235p2di"> · {s.unpaid.map(a => a.customer.name).join(', ')}</span>
                </li>}
              {s.openRequests.length > 0 && <li data-id="element-1632" data-mp="dsij3yh3ih6r">
                  <Link to={`${ADMIN_BASE}/inbox?tab=afspraken`} className="text-clay underline-offset-4 hover:underline" data-id="element-1633" data-mp="isdby7kflgug">
                    {plural(s.openRequests.length, 'aanvraag', 'aanvragen')} voor {name} wacht{s.openRequests.length === 1 ? '' : 'en'} op antwoord
                  </Link>
                </li>}
            </ul>}
        </>}
    </Panel>;
}